/**
 * batch-68 integratie (ADDENDA gedeelde bestanden 8; FO-346 §2 "gedeeld, niet
 * gedupliceerd") - de twee registratie-detectoren op één plek:
 *
 *   - **ontbrekende registratie**: FO-342 regel 1 (verloningscontrole, variant
 *     DAG) en FO-346 §3.4 "bon ontbreekt" (gespreksvoorbereiding);
 *   - **planning tegenover registratie**: FO-342 regel 3 en FO-346 §3.3/§3.4
 *     "gepland tegenover gewerkt" / "bon wijkt af van planning".
 *
 * Wat per module verschilt, levert de aanroeper: WANNEER een dag telt (payroll:
 * dagen vóór vandaag in de urenmaand; HRM: na de invuldeadline), welke
 * afwezigheid een dag verklaart, de granulariteit van de melding (per dagregel
 * of per dag) en de drempel (Payroll > Controle resp. HRM-instellingen: beide
 * FO's noemen hun eigen instelling).
 *
 * Gelijk in beide: een afwezig gemelde dagregel en een dagregel zonder
 * urenplicht per dag (consignatie) zijn nooit "zonder registratie" en tellen
 * niet mee tegen de planning.
 *
 * Puur: geen database, geen klok.
 */

import { round2 } from './leave-calculations'

/** Eén werkbon-dagregel (`WerkbonDayEntry`) zoals beide detectoren hem lezen. */
export interface RegistratieDagregel {
  /** Op de werkbon als afwezig gemarkeerd: geen werk, dus nooit "ontbrekend". */
  isAfwezig: boolean
  /**
   * Geen urenplicht per dag: een consignatienacht (piket, `Werkbon.isConsignatie`).
   * De nacht staat gepland als 22:00-06:00 met 0 uur, een oproep zet alleen de
   * uren (nooit de tijden) en goedkeuren sluit de dagregel. Net als bij de
   * werkbon-volledigheid (FO-105, `checkWerkbonVolledigheid`) is zo'n dagregel
   * nooit "zonder registratie" en is hij geen planning om tegen te vergelijken.
   */
  geenUrenplicht: boolean
  /** Werkelijke begin- en eindtijd ("HH:mm"), null als niet ingevuld. */
  actualStart: string | null
  actualEnd: string | null
  /** Status GESLOTEN: verwerkt zonder tijden, telt als geregistreerd. */
  gesloten: boolean
  /** Betaalde geplande uren (span min de pauze-aftrek, FO-473); null = planning onleesbaar. */
  geplandeUren: number | null
  /** Geregistreerde uren (`WerkbonDayEntry.hours`). */
  uren: number
}

/**
 * Een geplande dagregel zonder enige registratie: geen tijden, niet gesloten,
 * niet afwezig en met urenplicht per dag.
 */
export function dagregelZonderRegistratie(r: RegistratieDagregel): boolean {
  return !r.geenUrenplicht && !r.isAfwezig && !r.gesloten && r.actualStart == null && r.actualEnd == null
}

/**
 * Een dagregel met registratie (begintijd ingevuld of gesloten), die meetelt
 * tegen de planning. Niet afwezig en met urenplicht per dag.
 */
export function dagregelMetRegistratie(r: RegistratieDagregel): boolean {
  return !r.geenUrenplicht && !r.isAfwezig && (r.actualStart != null || r.gesloten)
}

export interface PlanningVergelijking {
  /** Som van de geplande betaalde uren van de geregistreerde dagregels. */
  gepland: number
  /** Som van de geregistreerde uren van diezelfde dagregels. */
  geregistreerd: number
  /** geregistreerd - gepland, op twee decimalen. */
  verschil: number
}

/**
 * Planning tegenover registratie voor één dag (alle dagregels van één
 * medewerker op die kalenderdag). Alleen dagregels met registratie tellen
 * (`dagregelMetRegistratie`: niet afwezig, niet consignatie); geen enkele =
 * niets te vergelijken (zonder registratie is dat de ontbrekende registratie).
 * Is de planning van een geregistreerde dagregel onleesbaar, dan is er geen
 * betrouwbare vergelijking en geeft de functie ook null.
 */
export function planningVergelijking(regels: ReadonlyArray<RegistratieDagregel>): PlanningVergelijking | null {
  const geregistreerd = regels.filter(dagregelMetRegistratie)
  if (geregistreerd.length === 0) return null
  let gepland = 0
  let uren = 0
  for (const r of geregistreerd) {
    if (r.geplandeUren == null) return null
    gepland += r.geplandeUren
    uren += r.uren
  }
  return { gepland: round2(gepland), geregistreerd: round2(uren), verschil: round2(uren - gepland) }
}

/** Wijkt het verschil (in uren, beide richtingen) méér af dan de drempel? */
export function wijktAfVanPlanning(verschil: number, drempelUren: number): boolean {
  return Math.abs(verschil) > drempelUren + 1e-9
}
